import { Record, List } from 'immutable';
import moment from 'moment';

import ValidityPeriod from '../ValidityPeriod';
import OrderLeg from './Leg';
import { construct, fields, resolveAll } from '../../helpers/LinkMapper';

const defaults = {
    id: null,
    validFrom: null,
    validTo: null,
    legs: new List(),
    validityPeriod: null
};

class OrderJourney extends Record(defaults) { // eslint-disable-line new-cap

    static fromJSON (json = {}) {
        return construct(OrderJourney, json, {
            id: fields.string,
            validFrom: fields.datetime,
            validTo: fields.datetime,
            legs: resolveAll.as(OrderLeg),
            validityPeriod: fields.string
        });
    }

    getDepartureTime () {
        let transfer = 0;
        let current = this.legs.first();
        let index = 0;
        while (current && !current.departure) {
            transfer += current.transferTime;
            index++;
            current = this.legs.get(index);
        }

        return current ? current.departure.clone().subtract(transfer, 'minutes') : null;
    }

    getArrivalTime () {
        let transfer = 0;
        let current = this.legs.last();
        let index = this.legs.size - 1;
        while (current && !current.arrival) {
            transfer += current.transferTime;
            index--;
            current = this.legs.get(index);
        }

        return current ? current.arrival.clone().add(transfer, 'minutes') : null;
    }

    getOrigin () {
        return this.legs.first().origin;
    }

    getDestination () {
        return this.legs.last().destination;
    }

    getChanges () {
        return this.legs.size - 1; //TODO: get the api to return this?
    }

    hasSeats () {
        return this.legs.some((leg) => leg.hasSeats());
    }

    getValidityPeriod () {
        return ValidityPeriod.fromString(this.validityPeriod);
    }

    getBikeSeats () {
        return this.legs.reduce((bikeSeats, leg) => {
            const legBikeSeats = leg.getBikeSeats();
            if (legBikeSeats) {
                return bikeSeats.concat(legBikeSeats);
            }
            return bikeSeats;
        }, new List());
    }

    /*
        * Returns the maximum number of bike seats available across all legs.
        * This is useful for determining the total capacity for bike reservations
        * in the entire journey.
        *
        * @returns {number} The maximum number of bike seats available.
    */
    countBikeSeats () {
        return this.legs.reduce((max, leg) => {
            const bikeSeats = leg.bikeSeats;
            const count = (bikeSeats && typeof bikeSeats.count === 'function') ? bikeSeats.count() : 0;
            return Math.max(max, count);
        }, 0);
    }

    getSeatReservations () {
        return this.legs.reduce((seats, leg) => {
            const legSeats = leg.seats;
            if (legSeats) {
                return seats.concat(legSeats);
            }
            return seats;
        }, new List());
    }

    /*
        * Returns the maximum number of seat reservations available across all legs.
        * This is useful for determining the total capacity for seat reservations
        * in the entire journey.
        *
        * @returns {number} The maximum number of seat reservations available.
    */
    countSeatReservations () {
        return this.legs.reduce((max, leg) => {
            const seats = leg.seats;
            const hasCountedPlaceSeats = leg.hasCountedPlaceSeats();
            const count = (seats && typeof seats.count === 'function' && !hasCountedPlaceSeats) ? seats.count() : 0;
            return Math.max(max, count);
        }, 0);
    }

    getSeatStatus() {
        if (!this.legs || this.legs.size === 0) { return null; }
        const trainLegs = this.legs.filter(leg => leg.isTrain());
        if (trainLegs.size === 0) { return null; }
        const legsWithAssignedSeats = trainLegs.filter(leg => leg.hasSeats() && !leg.hasCountedPlaceSeats()).size;
        if (legsWithAssignedSeats === trainLegs.size) { return 'available'; }
        if (legsWithAssignedSeats === 0) { return 'unavailable'; }
        return 'partial';
    }

    isExpired() {
        return moment(this.validTo).diff(moment(), 'days') < 0;
    }
}


export default OrderJourney;
